/**
 * Web analog of ru.esoft.wf.client.constants.LocalProperties.
 */

const STORAGE_KEY = 'wf_local_properties'
const DEBOUNCE_MS = 300

export type ElementStateValue = Record<string, unknown>

export interface LocalPropertiesStorage {
  ELEMENT_STATE?: Record<string, ElementStateValue>
  VERIFY_SIGN?: boolean
  [key: string]: unknown
}

function loadFromStorage(): LocalPropertiesStorage {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
    if (raw) {
      const parsed = JSON.parse(raw) as LocalPropertiesStorage
      return typeof parsed === 'object' && parsed !== null ? parsed : {}
    }
  } catch {
    // ignore
  }
  return {}
}

function saveToStorage(data: LocalPropertiesStorage): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    }
  } catch {
    // ignore
  }
}

/** In-memory store; synced with localStorage on load and saveAll(). */
let store: LocalPropertiesStorage = loadFromStorage()

/** Pending debounced save timeout; cleared on saveAll() or when timer fires. */
let saveTimeoutId: ReturnType<typeof setTimeout> | null = null

function getStore(): LocalPropertiesStorage {
  if (!store.ELEMENT_STATE || typeof store.ELEMENT_STATE !== 'object') {
    store.ELEMENT_STATE = {}
  }
  return store
}

function markDirty(): void {
  if (saveTimeoutId !== null) {
    clearTimeout(saveTimeoutId)
  }
  saveTimeoutId = setTimeout(() => {
    saveToStorage(getStore())
    saveTimeoutId = null
  }, DEBOUNCE_MS)
}

/**
 * ELEMENT_STATE: map of element id (string) -> state object.
 * Compatible with Java LocalProperties.ELEMENT_STATE (get/set/clear).
 */
export const ELEMENT_STATE = {
  get(keys?: string[]): Record<string, unknown> {
    const elementState = getStore().ELEMENT_STATE ?? {}
    if (!keys || keys.length === 0) {
      return { ...elementState }
    }
    const result: Record<string, unknown> = {}
    for (const k of keys) {
      if (Object.prototype.hasOwnProperty.call(elementState, k)) {
        result[k] = elementState[k]
      }
    }
    return result
  },

  set(keys: string[], value: unknown): void {
    const s = getStore()
    if (!s.ELEMENT_STATE) s.ELEMENT_STATE = {}
    const key = keys && keys.length > 0 ? keys.join('.') : ''
    if (key) {
      ;(s.ELEMENT_STATE as Record<string, unknown>)[key] = value as ElementStateValue
      markDirty()
    }
  },

  clear(ids: string[]): void {
    if (!ids || ids.length === 0) return
    const s = getStore()
    if (!s.ELEMENT_STATE) return
    for (const id of ids) {
      delete (s.ELEMENT_STATE as Record<string, unknown>)[id]
    }
    markDirty()
  }
}

export function saveAll(): void {
  if (saveTimeoutId !== null) {
    clearTimeout(saveTimeoutId)
    saveTimeoutId = null
  }
  saveToStorage(getStore())
}

/**
 * Get a named property by type
 */
export function get(type: string): unknown {
  const s = getStore()
  switch (type) {
    case 'verifySign':
      return s.VERIFY_SIGN !== undefined ? s.VERIFY_SIGN : true
    default:
      return (s as Record<string, unknown>)[type]
  }
}

/**
 * Set a named property
 */
export function set(type: string, value: unknown): void {
  const s = getStore()
  ;(s as Record<string, unknown>)[type] = value
  markDirty()
}

/**
 * Returns current element_states map for sending to server (clientData.element_states).
 * Keys are element ids (string); values are FormState objects.
 */
export function getElementStates(): Record<string, unknown> {
  const elementState = getStore().ELEMENT_STATE ?? {}
  return { ...elementState }
}

/**
 * API object to expose as window.LocalProperties / Packages.ru.esoft.wf.client.constants.LocalProperties. 
 */
function makeLocalPropertiesApi(): typeof LocalPropertiesApiBase & Record<string, { get: (defaultValue?: unknown) => unknown }> {
  const base: typeof LocalPropertiesApiBase = {
    ELEMENT_STATE,
    saveAll,
    get,
    set,
    getElementStates
  }
  return new Proxy(base, {
    get (target, prop: string) {
      if (Object.prototype.hasOwnProperty.call(target, prop)) {
        return (target as Record<string, unknown>)[prop]
      }
      return {
        get (defaultValue?: unknown): unknown {
          const value = getStore()[prop]
          return value !== undefined && value !== null ? value : defaultValue
        }
      }
    }
  }) as typeof LocalPropertiesApiBase & Record<string, { get: (defaultValue?: unknown) => unknown }>
}

const LocalPropertiesApiBase = {
  ELEMENT_STATE,
  saveAll,
  get,
  set,
  getElementStates
}

export type LocalPropertiesApi = ReturnType<typeof makeLocalPropertiesApi>

export const localPropertiesService = {
  ELEMENT_STATE,
  saveAll,
  get,
  set,
  getElementStates,
  /** For GlobalProvider — returns API with Proxy for script keys like DOCUMENTS_SPECIAL_FILTER_ENABLE.get(false) */
  getApi: makeLocalPropertiesApi
}
